import { Box, Divider, Tab, Typography } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import ComputerIcon from "@mui/icons-material/ComputerOutlined";
import ImportDevicesIcon from "@mui/icons-material/ImportantDevicesOutlined";
import AppSettingsAlt from "@mui/icons-material/AppSettingsAlt";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProgressIndicator from "../../common/progressIndicator/ProgressIndicator";
import useSession from "../../effects/useSession";
import { ApplicationState } from "../../store";
import { fetchRequest as fetchStats } from "../../store/stats/actions";
import { fetchRequest as fetchCardTypes } from "../../store/cardType/actions";
import Layout from "../Layout";
import pageStyles from "../pageStyles";
import TotalStatsItem from "./TotalStatsItem";
import {
  AddToHomeScreen,
  Memory,
  PermDeviceInformation,
} from "@mui/icons-material";
import PieChartComponent from "./charts/PieChartComponent";
import BarChartComponent from "./charts/BarChartComponent";
import { isNil } from "ramda";
import ViewOption from "./ViewOption";
import useUserAccess from "../../effects/useUserAccess";
import { get } from "lodash";

export type ChartData = {
  name: string | null;
  value?: number | null;
  count?: number | null;
};

interface BarChartDataProps {
  data?: ChartData[];
  isVerticalChart?: boolean;
  activeTab: TabNav;
}

interface BarChartTab extends BarChartDataProps {
  label: string;
}

enum TabNav {
  SoftwareInUse,
  TopEMVs,
  TopCPAT,
}

const dashboardClasses = {
  main: {
    height: "100vh",
  },
  totalStatsWrapper: {
    background: "#fff",
    borderRadius: 1,
    padding: 3,
    marginBottom: 4,
    justifyContent: "center",
  },
  sectionTitle: {
    padding: 2,
    fontSize: 14,
    fontWeight: 600,
  },
  sectionWrapper: {
    background: "#fff",
    borderRadius: 1,
  },
  tabNav: {
    fontSize: 13,
    color: "#555",
    cursor: "pointer",
    borderBottom: "1px solid transparent",
    transition: "border-bottom 0.2s ease-in-out",
  },
  tabNavActive: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--dmg-primary)",
    cursor: "pointer",
    borderBottom: "1px solid var(--dmg-primary)",
    transition: "border-bottom 0.2s ease-in-out",
  },
};

export const chartColors = [
  "var(--dmg-blue-primary)",      // #1a3a6b - Primary blue
  "var(--dmg-purple)",            // #DA007C - Purple accent
  "var(--dmg-blue-secondary)",    // #1641e4 - Teal accent
  "var(--dmg-blue-light)",        // #5494DC - Light blue
  "var(--dmg-blue-secondary)",    //rgb(29, 35, 45) - Secondary blue
  "var(--dmg-blue-accent)",       // #1976D2 - Blue accent
];

export const queryLimit = 10;

const ACTIVE_TAB = {
  dashboard: '1',
};

function Dashboard() {
  const dispatch = useDispatch();
  const classes = pageStyles();
  const session = useSession();
  const [itemsPerChart, setItemsPerChart] = useState(10);

  const { loading, data: stats } = useSelector(
    (state: ApplicationState) => state.stats
  );
  const { data: userData } = useSelector((state: ApplicationState) => state.user);

  const [activeDashBoardTab, setActiveTab] = useState(ACTIVE_TAB.dashboard);
  const [barChartData, setBarChartData] = useState<BarChartDataProps>({
    data: [],
    activeTab: TabNav.SoftwareInUse,
  });

  useEffect(() => {
    dispatch(fetchStats(null, { limit: queryLimit }));
    dispatch(fetchCardTypes(null));
  }, [dispatch]);

  useEffect(() => {
    if (stats?.data?.[0]) {
      setBarChartData({
        data: stats.data[0].terminalStats.softwareInUse,
        activeTab: TabNav.SoftwareInUse,
      });
    }
  }, [stats]);

  const handleOnchangeTab = (e, value) => {
    setActiveTab(value)
  }

  // CHARTS
  const standaloneIntegratedTerminal: ChartData[] = [
    {
      name: "Integrated Terminals",
      value: stats?.data?.[0]?.terminalStats.integratedTerminal || 0,
    },
    {
      name: "Standalone Terminals",
      value: stats?.data?.[0]?.terminalStats.standaloneTerminal || 0,
    },
  ];

  const barChartTabData: BarChartTab[] = [
    {
      label: "Software in Use",
      data: stats?.data?.[0]?.terminalStats.softwareInUse || [],
      activeTab: TabNav.SoftwareInUse,
    },
    {
      label: "Top EMVs",
      data: stats?.data?.[0]?.terminalStats.topEmvs || [],
      activeTab: TabNav.TopEMVs,
    },
    {
      label: "Top CPATs",
      data: stats?.data?.[0]?.terminalStats.topCpats || [],
      activeTab: TabNav.TopCPAT,
    },
  ];

  return (
    <Layout sessionData={session}>
      <main className={`${classes.content} ${dashboardClasses.main}`}>
        <div className={classes.appBarSpacer as string} />
        <Container maxWidth="lg" className={classes.container as string}>
          <TabContext value={activeDashBoardTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper'  }}>
              <TabList onChange={handleOnchangeTab}>
                <Tab label="Stats" value={ACTIVE_TAB.dashboard} />
              </TabList>
            </Box>
            <TabPanel value={ACTIVE_TAB.dashboard}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                boxShadow={1}
                sx={dashboardClasses.totalStatsWrapper}
              >
                {loading || isNil(stats?.data?.[0]) ? (
                  <ProgressIndicator noPadding />
                ) : (
                  <>
                    <TotalStatsItem
                      title="Total Merchants"
                      value={stats.data[0].totals.totalMerchant}
                      icon={<StorefrontIcon />}
                    />
                    <TotalStatsItem
                      title="Total Terminals"
                      value={stats.data[0].totals.totalTerminal}
                      icon={<ImportDevicesIcon />}
                    />
                    <TotalStatsItem
                      title="Staged Terminals"
                      value={stats.data[0].totals.totalStagedTerminal}
                      icon={<AppSettingsAlt />}
                    />
                    <TotalStatsItem
                      title="Configured Terminals"
                      value={stats.data[0].totals.totalTerminalConfigured}
                      icon={<AddToHomeScreen />}
                    />
                    <TotalStatsItem
                      title="Suspended Terminals"
                      value={stats.data[0].totals.totalSuspendedTerminal}
                      icon={<PermDeviceInformation />}
                    />
                    <TotalStatsItem
                        title="Total POS's"
                        value={stats.data[0].totals.totalPOS}
                        icon={<ComputerIcon />}
                    />
                    <TotalStatsItem
                      title="Total Processors"
                      value={stats.data[0].totals.totalProcessor}
                      icon={<Memory />}
                      noBorder
                    />
                  </>
                )}
              </Box>

              <Grid container spacing={3}>
                {/* main col */}
                <Grid item xs={12} md={8}>
                  <Box sx={dashboardClasses.sectionWrapper} boxShadow={1}>
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box display="flex">
                          {barChartTabData.map((tabData, idx) => (
                            <Box
                              key={idx}
                              padding={2}
                              sx={
                                barChartData.activeTab === tabData.activeTab
                                  ? dashboardClasses.tabNavActive
                                  : dashboardClasses.tabNav
                              }
                              onClick={() =>
                                setBarChartData({
                                  data: tabData.data,
                                  isVerticalChart: tabData.isVerticalChart,
                                  activeTab: tabData.activeTab,
                                })
                              }
                            >
                              {tabData.label}
                            </Box>
                          ))}
                        </Box>
                        <Box>
                          <ViewOption
                            itemsPerChart={itemsPerChart}
                            setItemsPerChart={setItemsPerChart}
                          />
                        </Box>
                      </Box>
                      <Divider />
                    </Box>
                    <BarChartComponent
                      data={barChartData.data?.slice(0, itemsPerChart)}
                      chartHeight={350}
                      chartWidth={600}
                      isVerticalChart={barChartData.isVerticalChart || false}
                      loading={loading}
                    />
                  </Box>
                </Grid>

                {/* right side col */}
                <Grid item xs={12} md={4}>
                  <Box sx={dashboardClasses.sectionWrapper} boxShadow={1}>
                    <Box>
                      <Typography sx={dashboardClasses.sectionTitle}>
                        Integrated vs. Standalone Terminals
                      </Typography>
                      <Divider />
                    </Box>
                    <PieChartComponent
                      data={standaloneIntegratedTerminal}
                      chartWidth={250}
                      chartHeight={250}
                      loading={loading}
                    />
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>
          </TabContext>
        </Container>
      </main>
    </Layout>
  );
}

export default memo(Dashboard);
